import {
  ActionEmptyState,
  Badge,
  Button,
  LoadingSpinner,
  Text,
  Typography,
} from "@/components/ui";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/shadcn/Accordion/Accordion";
import { GenerateMassTortFilesModal } from "@/components/features/mass-torts/GenerateMassTortFilesModal";
import { retrieveMassTortTemplateGenerationRequestQueryOptions } from "@/lib/data/react-query/mass-torts/mass-tort";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import type {
  MassTort,
  MassTortCustomField,
  MassTortGeneratedFileTemplateRequest,
} from "@/lib/types";
import { useQueries, useQuery } from "@tanstack/react-query";
import { FileText, FilePlus2, RefreshCcw, TriangleAlert } from "lucide-react";
import { useState } from "react";
import pluralize from "pluralize";

type MassTortFileGenerationViewProps = {
  massTortId: string;
  massTort: MassTort;
  massTortCustomFields: MassTortCustomField[];
  generationRequests: MassTortGeneratedFileTemplateRequest[];
};

const generationRequestStatusConfig = {
  processing: {
    label: "Processing",
    className: "border-yellow-300 bg-yellow-50 text-yellow-800",
  },
  completed: {
    label: "Completed",
    className: "border-green-300 bg-green-50 text-green-800",
  },
  completed_with_errors: {
    label: "Completed with errors",
    className: "border-red-300 bg-red-50 text-red-800",
  },
} as const;

function getGenerationRequestStatusDisplay(status?: string | null) {
  if (status && status in generationRequestStatusConfig) {
    return generationRequestStatusConfig[
      status as keyof typeof generationRequestStatusConfig
    ];
  }

  return {
    label: "Unknown",
    className: "border-muted-foreground/30 bg-muted text-muted-foreground",
  };
}

export function MassTortFileGenerationView({
  massTortId,
  massTort,
  massTortCustomFields,
  generationRequests,
}: MassTortFileGenerationViewProps) {
  const [isGenerateFilesOpen, setIsGenerateFilesOpen] = useState(false);
  const hasGenerationRequests = generationRequests.length > 0;

  return (
    <>
      <div className="flex h-screen min-h-0 flex-col overflow-auto p-4 sm:p-6">
        <div className="w-full">
          <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="gap-y-1">
              <Text variant="title">
                Generation requests ({generationRequests.length})
              </Text>
              <Text variant="subHeading" className="mt-0.5">
                Track mass tort file generation requests and retry failed jobs.
              </Text>
            </div>
            <Button
              variant="foreground"
              size="md"
              className="shrink-0 gap-2"
              onClick={() => {
                setIsGenerateFilesOpen(true);
              }}
            >
              <FilePlus2 className="h-4 w-4" />
              Generate files
            </Button>
          </div>

          {hasGenerationRequests ? (
            <div className="space-y-3">
              {generationRequests.map((request) => (
                <MassTortFileGenerationRequestRow
                  key={request.id}
                  request={request}
                  massTortCustomFields={massTortCustomFields}
                />
              ))}
            </div>
          ) : (
            <ActionEmptyState
              className="min-h-[360px] justify-center"
              icon={<FilePlus2 className="h-6 w-6" />}
              title="No file generation requests yet"
              description="Start a generation request to create files across this mass tort's matters."
            />
          )}
        </div>
      </div>
      <GenerateMassTortFilesModal
        mode="generate"
        open={isGenerateFilesOpen}
        setOpen={setIsGenerateFilesOpen}
        massTortId={massTortId}
        massTort={massTort}
        massTortCustomFields={massTortCustomFields}
      />
    </>
  );
}

function MassTortFileGenerationRequestRow({
  request,
  massTortCustomFields,
}: {
  request: MassTortGeneratedFileTemplateRequest;
  massTortCustomFields: MassTortCustomField[];
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [retryTarget, setRetryTarget] = useState<{
    matterId?: string;
    matterTitle?: string;
  } | null>(null);
  const massTortId = request.massTortId;
  const requestId = request.id;
  const failedJobs = request.failedJobs;
  const canExpand = failedJobs > 0 && Boolean(massTortId && requestId);
  const { data, isError, isLoading } = useQuery({
    ...retrieveMassTortTemplateGenerationRequestQueryOptions({
      massTortId,
      requestId,
    }),
    enabled: canExpand && (isExpanded || Boolean(retryTarget)),
  });
  const detailedRequest = data?.data;
  const failedJobDetails = detailedRequest?.failedJobDetails ?? [];
  const failedJobGroups = groupFailedJobDetails(failedJobDetails);
  const matterQueries = useQueries({
    queries: failedJobDetails.map((failedJob) => ({
      ...retrieveMatterQueryOptions(failedJob.matterId),
      enabled: isExpanded && canExpand && Boolean(failedJob.matterId),
    })),
  });
  const isMatterTitlesLoading = matterQueries.some((query) => query.isLoading);
  const matterTitleById = new Map(
    matterQueries
      .map((query) => query.data?.data)
      .filter(
        (matter): matter is NonNullable<typeof matter> & { matterId: string } =>
          typeof matter?.matterId === "string",
      )
      .map((matter) => [
        matter.matterId,
        typeof matter.matterTitle === "string"
          ? matter.matterTitle.trim()
          : undefined,
      ]),
  );
  const isDetailsLoading = isLoading || isMatterTitlesLoading;
  const retryRequest = detailedRequest ?? request;

  if (!canExpand) {
    return (
      <div className="border-border flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between">
        <MassTortFileGenerationRequestSummary request={request} />
        <MassTortFileGenerationRequestActions request={request} />
      </div>
    );
  }

  return (
    <>
      <Accordion
        type="single"
        collapsible
        value={isExpanded ? requestId : ""}
        onValueChange={(value) => {
          setIsExpanded(value === requestId);
        }}
      >
        <AccordionItem
          value={requestId}
          className="border-border overflow-hidden rounded-lg border"
        >
          <AccordionTrigger
            className="hover:bg-light-primary/50 cursor-pointer gap-3 px-4 py-4 hover:no-underline"
            iconPosition="left"
          >
            <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <MassTortFileGenerationRequestSummary request={request} />
              <MassTortFileGenerationRequestActions
                request={request}
                onRetry={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setRetryTarget({});
                }}
              />
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            {isDetailsLoading ? (
              <div className="text-muted-foreground flex items-center gap-2 text-sm">
                <LoadingSpinner />
                Loading failed jobs...
              </div>
            ) : isError ? (
              <Typography className="text-destructive text-sm">
                Unable to load failed job details.
              </Typography>
            ) : failedJobDetails.length ? (
              <MassTortFileGenerationFailedJobGroups
                failedJobGroups={failedJobGroups}
                getMatterTitle={(matterId) => matterTitleById.get(matterId)}
                onRetry={(failedJob, matterTitle) => {
                  setRetryTarget({
                    matterId: failedJob.matterId,
                    matterTitle,
                  });
                }}
              />
            ) : (
              <Typography className="text-muted-foreground text-sm">
                No failed job details were returned for this request.
              </Typography>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      <GenerateMassTortFilesModal
        mode="retry"
        open={Boolean(retryTarget)}
        setOpen={(open) => {
          if (!open) {
            setRetryTarget(null);
          }
        }}
        request={retryRequest}
        massTortCustomFields={massTortCustomFields}
        matterId={retryTarget?.matterId}
        matterTitle={retryTarget?.matterTitle}
      />
    </>
  );
}

type FailedJobDetail = NonNullable<
  MassTortGeneratedFileTemplateRequest["failedJobDetails"]
>[number];

type FailedJobDetailGroup = {
  id: string;
  failedJobs: FailedJobDetail[];
  missingVariables: string[];
  title: string;
  description: string;
};

function normalizeMissingVariables(missingVariables?: string[]) {
  return Array.from(
    new Set(
      (missingVariables ?? [])
        .map((variable) => variable.trim())
        .filter(Boolean),
    ),
  ).sort((firstVariable, secondVariable) =>
    firstVariable.localeCompare(secondVariable),
  );
}

function groupFailedJobDetails(failedJobDetails: FailedJobDetail[]) {
  const groups = new Map<string, FailedJobDetailGroup>();

  failedJobDetails.forEach((failedJob) => {
    const missingVariables = normalizeMissingVariables(
      failedJob.missingVariables,
    );
    const groupKey = missingVariables.length
      ? `missing-${JSON.stringify(missingVariables)}`
      : "other-errors";
    const existingGroup = groups.get(groupKey);

    if (existingGroup) {
      existingGroup.failedJobs.push(failedJob);
      return;
    }

    groups.set(groupKey, {
      id: groupKey,
      failedJobs: [failedJob],
      missingVariables,
      title: missingVariables.length
        ? `Missing ${pluralize("variable", missingVariables.length, true)}`
        : "Other errors",
      description: missingVariables.length
        ? "These failed jobs are missing the same template variables."
        : "These failed jobs had errors unrelated to missing template variables.",
    });
  });

  return Array.from(groups.values()).sort((firstGroup, secondGroup) => {
    if (!firstGroup.missingVariables.length) {
      return 1;
    }

    if (!secondGroup.missingVariables.length) {
      return -1;
    }

    return (
      secondGroup.failedJobs.length - firstGroup.failedJobs.length ||
      firstGroup.missingVariables
        .join(",")
        .localeCompare(secondGroup.missingVariables.join(","))
    );
  });
}

function MassTortFileGenerationFailedJobGroups({
  failedJobGroups,
  getMatterTitle,
  onRetry,
}: {
  failedJobGroups: FailedJobDetailGroup[];
  getMatterTitle: (matterId: string) => string | undefined;
  onRetry: (failedJob: FailedJobDetail, matterTitle?: string) => void;
}) {
  return (
    <Accordion type="multiple" className="space-y-3">
      {failedJobGroups.map((group) => (
        <AccordionItem
          key={group.id}
          value={group.id}
          className="border-border overflow-hidden rounded-lg border"
        >
          <AccordionTrigger
            className="hover:bg-muted/50 cursor-pointer gap-3 px-3 py-3 hover:no-underline"
            iconPosition="left"
          >
            <div className="flex min-w-0 flex-1 flex-col gap-2 text-left">
              <div className="flex flex-wrap items-center gap-2">
                <Typography className="font-medium">{group.title}</Typography>
                <Badge variant="outline">
                  {pluralize("failed job", group.failedJobs.length, true)}
                </Badge>
              </div>
              <Typography className="text-muted-foreground text-sm">
                {group.description}
              </Typography>
              {group.missingVariables.length ? (
                <div className="flex flex-wrap gap-2">
                  {group.missingVariables.map((variable) => (
                    <Badge key={variable} variant="outline">
                      {variable}
                    </Badge>
                  ))}
                </div>
              ) : null}
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-3 pb-3">
            <div className="space-y-2">
              {group.failedJobs.map((failedJob, failedJobIndex) => {
                const matterTitle = getMatterTitle(failedJob.matterId);
                return (
                  <MassTortFileGenerationFailedJobDetail
                    key={`${failedJob.matterId}-${failedJob.error}-${failedJobIndex}`}
                    failedJob={failedJob}
                    matterTitle={matterTitle}
                    showMissingVariables={false}
                    onRetry={() => {
                      onRetry(failedJob, matterTitle);
                    }}
                  />
                );
              })}
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

function MassTortFileGenerationFailedJobDetail({
  failedJob,
  matterTitle,
  showMissingVariables = true,
  onRetry,
}: {
  failedJob: FailedJobDetail;
  matterTitle?: string;
  showMissingVariables?: boolean;
  onRetry: () => void;
}) {
  return (
    <div className="bg-muted/30 border-border rounded-lg border p-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <Typography className="font-medium">
            {matterTitle || `Matter ID: ${failedJob.matterId}`}
          </Typography>
          <div className="flex items-start gap-1.5 text-sm text-amber-700">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <Typography className="text-sm">{failedJob.error}</Typography>
          </div>
        </div>
        <Button size="sm" variant="outline" className="gap-2" onClick={onRetry}>
          <RefreshCcw className="h-4 w-4" />
          Retry
        </Button>
      </div>
      {showMissingVariables && failedJob.missingVariables?.length ? (
        <Accordion type="single" collapsible className="mt-3">
          <AccordionItem value="missing-variables" className="border-b-0">
            <AccordionTrigger className="border-border bg-background text-foreground hover:bg-muted/60 w-fit flex-none cursor-pointer justify-start gap-1 rounded-md border px-2 py-1 text-xs font-medium uppercase shadow-xs transition-colors hover:no-underline">
              <span>
                Missing variables ({failedJob.missingVariables.length})
              </span>
            </AccordionTrigger>
            <AccordionContent className="pt-3 pb-0">
              <div className="flex flex-wrap gap-2">
                {failedJob.missingVariables.map((variable) => (
                  <Badge key={variable} variant="outline">
                    {variable}
                  </Badge>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      ) : null}
    </div>
  );
}

function MassTortFileGenerationRequestSummary({
  request,
}: {
  request: MassTortGeneratedFileTemplateRequest;
}) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <div
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-sky-100 bg-sky-50 text-sky-600"
        aria-hidden
      >
        <FileText className="h-4 w-4" />
      </div>
      <div className="min-w-0 space-y-1">
        <Typography className="font-medium">
          {request.name || "Generated file request"}
        </Typography>
        <Typography className="text-muted-foreground text-sm">
          {pluralize("total matter", request.totalJobs, true)}
        </Typography>
      </div>
    </div>
  );
}

function MassTortFileGenerationRequestActions({
  request,
  onRetry,
}: {
  request: MassTortGeneratedFileTemplateRequest;
  onRetry?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}) {
  const status = getGenerationRequestStatusDisplay(request.status);
  const showRetry =
    request.status === "completed_with_errors" && Boolean(onRetry);

  return (
    <div className="flex shrink-0 items-center gap-2 sm:ml-auto">
      <Badge variant="outline" className={status.className}>
        <span className="mr-1.5 h-2 w-2 rounded-full bg-current" />
        {status.label}
      </Badge>
      {showRetry ? (
        <Button
          size="sm"
          variant="outline"
          className="gap-2"
          onClick={onRetry}
          onPointerDown={(event) => {
            event.stopPropagation();
          }}
        >
          <RefreshCcw className="h-4 w-4" />
          Retry all
        </Button>
      ) : null}
    </div>
  );
}
